import { Link } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { useState, useEffect } from "react";
import { IMG } from "../lib/images";
import { useLanguage } from "../lib/LanguageContext";

const photos = [
  { src: IMG.poolSunloungers, label: "Pool · sun loungers" },
  { src: IMG.poolMountain, label: "Pool with mountain view" },
  { src: IMG.poolAerial, label: "Aerial pool view" },
  { src: IMG.poolPalm, label: "Pool with palm tree" },
  { src: IMG.aerialProperty, label: "Property from above" },
  { src: IMG.villaSign, label: "Villa Athina · garden sign" },
  { src: IMG.balconyMountain, label: "Balcony with mountain view" },
  { src: IMG.terraceStone, label: "Stone terrace" },
  { src: IMG.bedroomGarden, label: "Bedroom opening to garden" },
  { src: IMG.livingRoom, label: "Bright living room" },
  { src: IMG.livingArches, label: "Arched living room" },
  { src: IMG.livingEntry, label: "Living area entry" },
  { src: IMG.twinRoomDoor, label: "Twin room with terrace door" },
  { src: IMG.twinRoomBlue, label: "Twin room · blue" },
  { src: IMG.masterBed, label: "Master bedroom" },
  { src: IMG.bathroom, label: "Bathroom" },
  { src: IMG.cribRoom, label: "Family room with crib" },
];

export const GalleryPage = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "Gallery · Athina Villas";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    if (active !== null) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  return (
    <main
      data-testid="gallery-page"
      className="bg-[#faf9f6] text-[#0a192f] min-h-screen"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-[#faf9f6]/80 border-b border-[#0a192f]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="gallery-back-home"
            className="inline-flex items-center gap-3 text-[#0a192f] hover:text-[#1a5b73] transition-colors group"
          >
            <ArrowLeft
              className="h-4 w-4 transition-transform group-hover:-translate-x-1"
              strokeWidth={1.5}
            />
            <span className="text-[11px] uppercase tracking-[0.22em]">
              Back to home
            </span>
          </Link>
          <div className="flex items-baseline gap-2">
            <span className="font-serif italic text-2xl text-[#0a192f]">
              Athina
            </span>
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#0a192f]/70">
              {t("brand_villas")}
            </span>
          </div>
          <a
            href="/#contact"
            data-testid="gallery-book-cta"
            className="bg-[#1a5b73] hover:bg-[#134456] text-white px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] transition-colors"
          >
            {t("nav_book_now")}
          </a>
        </div>
      </div>

      {/* Header */}
      <section className="pt-20 md:pt-28 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#6b705c] mb-6">
            — {photos.length} photos
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] max-w-3xl">
            The full <em className="italic text-[#1a5b73]">gallery</em>
            <br />
            of Athina Villas.
          </h1>
          <p className="mt-8 text-[#4a4a4a] font-light text-base md:text-lg max-w-2xl leading-relaxed">
            Real photographs from the property — pool, gardens, apartments,
            bedrooms, bathrooms, and the Cretan coastline just down the road.
            Click any photo to view it full size.
          </p>
        </div>
      </section>

      {/* Masonry-style bento grid */}
      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] md:auto-rows-[240px] gap-3 md:gap-4">
            {photos.map((p, i) => {
              // Vary tile spans to create a bento effect
              const pattern = [
                "md:col-span-2 md:row-span-2",
                "",
                "",
                "md:col-span-2",
                "",
                "md:row-span-2",
                "",
                "md:col-span-2",
                "",
                "",
                "md:col-span-2 md:row-span-2",
                "",
                "",
                "md:col-span-2",
                "",
                "",
                "md:col-span-2",
              ];
              const span = pattern[i % pattern.length] || "";
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`gallery-page-tile-${i}`}
                  className={`group relative overflow-hidden rounded-2xl bg-[#e8e3d9] ${span}`}
                >
                  <img
                    src={p.src}
                    alt={p.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-left translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/80">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-serif text-base md:text-lg text-white leading-snug">
                      {p.label}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#0a192f] text-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#c9d8c5] mb-6">
            — Ready to visit?
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05]">
            Make these views part of your{" "}
            <em className="italic text-[#7fb2c1]">next stay</em>.
          </h2>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <a
              href="/#contact"
              data-testid="gallery-footer-book"
              className="bg-[#faf9f6] text-[#0a192f] hover:bg-white px-9 py-4 rounded-full text-[12px] uppercase tracking-[0.22em] font-medium transition-colors"
            >
              {t("hero_cta_book")}
            </a>
            <Link
              to="/"
              data-testid="gallery-footer-home"
              className="border border-white/60 text-white hover:bg-white hover:text-[#0a192f] px-9 py-4 rounded-full text-[12px] uppercase tracking-[0.22em] font-medium transition-colors"
            >
              Back to home
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {active !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-[#0a192f]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setActive(null)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActive(null);
            }}
            data-testid="gallery-lightbox-close"
            aria-label="Close"
            className="absolute top-5 right-5 h-12 w-12 rounded-full border border-white/30 text-white hover:bg-white hover:text-[#0a192f] inline-flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i - 1 + photos.length) % photos.length);
            }}
            data-testid="gallery-lightbox-prev"
            aria-label="Previous"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-white/30 text-white hover:bg-white hover:text-[#0a192f] inline-flex items-center justify-center transition-colors"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i + 1) % photos.length);
            }}
            data-testid="gallery-lightbox-next"
            aria-label="Next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-white/30 text-white hover:bg-white hover:text-[#0a192f] inline-flex items-center justify-center transition-colors"
          >
            ›
          </button>
          <figure
            className="relative max-w-6xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photos[active].src}
              alt={photos[active].label}
              className="max-h-[80vh] w-full object-contain rounded-2xl"
            />
            <figcaption className="mt-4 text-center text-white/80 text-sm">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#c9d8c5] mr-3">
                {String(active + 1).padStart(2, "0")} / {photos.length}
              </span>
              {photos[active].label}
            </figcaption>
          </figure>
        </div>
      )}
    </main>
  );
};
